/**
 * Pillar capability carousel.
 *
 * Port of the rAF crawl from the original pages' React logic class. Kept
 * faithful to the original, including its constants and its wrap point:
 *
 *   - 34 px/s default drift (configurable per section)
 *   - prev/next add a one-card "boost" that decays, rather than jumping
 *   - the crawl wraps at scrollWidth / 2, exactly as capHalf() did
 *   - IntersectionObserver with rootMargin 120px halts it off screen
 *   - pointer over the track (or a touch) pauses it
 *   - the dot index is derived from scrollLeft, re-measured on resize
 *
 * Two departures, both because the original never let anyone else scroll the
 * track: the crawl picks up from wherever a swipe, trackpad or keyboard left
 * it, and after a touch it waits for momentum to settle before moving again.
 * With prefers-reduced-motion the drift stays at 0 and the arrows jump.
 *
 * The wrap at scrollWidth/2 is correct because the section renders the cards
 * twice — a real set plus an aria-hidden copy — so wrapping at the halfway
 * point lands exactly where the first set began and the loop is seamless.
 */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.dataset.capBound === '1') return;
    root.dataset.capBound = '1';

    var track = root.querySelector('[data-cap-track]');
    var dots = root.querySelector('[data-cap-dots]');
    if (!track) return;

    var cards = track.querySelectorAll('[data-cap-card]');
    if (!cards.length) return;

    // The section fades the track's left edge only once it has moved: at rest
    // the first card sits flush with the heading and should stay crisp.
    function markScrolled() {
      if (track.scrollLeft > 4) track.setAttribute('data-scrolled', '');
      else track.removeAttribute('data-scrolled');
    }
    track.addEventListener('scroll', markScrolled, { passive: true });
    markScrolled();

    var SPEED = parseFloat(root.getAttribute('data-speed'));
    if (isNaN(SPEED)) SPEED = 34;
    // Live query, so switching the OS setting takes effect without a reload.
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

    var raf = null;
    var lastTs = null;
    var pos = null;
    // scrollLeft as it read straight after this script last wrote it. If the
    // track is anywhere else, something other than the crawl moved it.
    var lastSet = null;
    var boost = 0;
    var paused = false;
    // Separate from paused, which the pointer owns: a touch holds the crawl
    // until resumeT fires, and a mouseout must not cut that short.
    var held = false;
    var resumeT = null;
    var RESUME_MS = 1500;
    var offscreen = false;
    var halfPx = null;
    var halfTs = 0;
    var stepPx = null;
    var idxTs = 0;
    var index = -1;

    function step() {
      if (stepPx) return stepPx;
      var card = track.querySelector('[data-cap-card]');
      if (!card) return 0;
      var gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
      stepPx = card.getBoundingClientRect().width + gap;
      return stepPx;
    }

    function half() {
      return track.scrollWidth / 2;
    }

    function setIndex(i) {
      if (i === index) return;
      index = i;
      if (dots) dots.setAttribute('data-index', String(i));
    }

    function indexAt(x) {
      var sp = step();
      var count = cards.length;
      if (sp > 0 && count > 0) {
        setIndex(((Math.round(x / sp) % count) + count) % count);
      }
    }

    function tick(ts) {
      raf = requestAnimationFrame(tick);
      var last = lastTs;
      lastTs = ts;
      if (last == null) return;

      var dt = Math.min(0.05, (ts - last) / 1000);
      if (halfPx == null || ts - halfTs > 2000) {
        halfPx = half();
        halfTs = ts;
      }
      var h = halfPx;
      if (h <= 0) return;
      if (pos == null) pos = lastSet = track.scrollLeft;

      // pos only ever came from scrollLeft once, so after a swipe the next
      // frame wrote the old position straight back and the track snapped to
      // where it started. Re-sync whenever the track is not where it was left.
      // The 2px dead band is for rounding: scrollLeft snaps the sub-pixel
      // steps below to whole or device pixels, which is also why pos is kept
      // at all -- re-reading it every frame would stall a 0.57px-a-frame crawl.
      // The dots follow the swipe rather than waiting for the crawl to resume.
      if (Math.abs(track.scrollLeft - lastSet) > 2) {
        pos = track.scrollLeft;
        indexAt(pos);
      }

      var reduce = reduceMotion && reduceMotion.matches;
      var delta = paused || held || offscreen || reduce ? 0 : SPEED * dt;

      if (boost) {
        var rate = 1 - Math.pow(0.0005, dt);
        var stepped = boost * rate;
        var applied = reduce || Math.abs(stepped) < 1.5 * dt * 60 ? boost : stepped;
        delta += applied;
        boost -= applied;
        if (Math.abs(boost) < 0.5) boost = 0;
      }

      if (!delta) return;

      var next = pos + delta;
      if (next >= h) next -= h;
      if (next < 0) next += h;
      pos = next;
      track.scrollLeft = next;
      // Read back rather than storing next: the browser rounds the write, and
      // clamps it when fewer cards than fit leave nothing to scroll into.
      lastSet = track.scrollLeft;

      if (ts - idxTs > 250) {
        idxTs = ts;
        indexAt(next);
      }
    }

    var navs = root.querySelectorAll('[data-cap-nav]');
    for (var i = 0; i < navs.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          var dir = btn.getAttribute('data-cap-nav') === 'prev' ? -1 : 1;
          boost += dir * step();
        });
      })(navs[i]);
    }

    track.addEventListener('mouseover', function () { paused = true; });
    track.addEventListener('mouseout', function () { paused = false; });

    // iOS momentum carries a swipe on well after the finger lifts, and
    // resuming on touchend wrote the crawl over the top of it. Hold until the
    // track has been still for RESUME_MS; every scroll while the hold is
    // pending starts that wait again.
    function hold() {
      clearTimeout(resumeT);
      resumeT = null;
      held = true;
    }
    function release() {
      clearTimeout(resumeT);
      resumeT = setTimeout(function () {
        resumeT = null;
        held = false;
      }, RESUME_MS);
    }
    track.addEventListener('touchstart', hold, { passive: true });
    track.addEventListener('touchend', release, { passive: true });
    track.addEventListener('touchcancel', release, { passive: true });
    track.addEventListener('scroll', function () {
      if (resumeT !== null) release();
    }, { passive: true });

    window.addEventListener('resize', function () {
      stepPx = null;
      halfPx = null;
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) { offscreen = !entries[0].isIntersecting; },
        { rootMargin: '120px' }
      ).observe(root);
    }

    setIndex(0);
    // Always run the loop: even with the drift set to 0, the arrows still need
    // it to animate their boost.
    raf = requestAnimationFrame(tick);
  }

  function boot() {
    var roots = document.querySelectorAll('[data-cap-root]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
